import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const faqs = [
    {
      q: '韩漫网是什么？',
      a: '韩漫网是一个专注于韩国漫画在线阅读的免费平台，我们精选优质韩漫作品，为用户提供高清、流畅的阅读体验。平台涵盖恋爱、奇幻、热血、搞笑、悬疑、都市等多种类型，满足不同读者的喜好。',
    },
    {
      q: '在韩漫网阅读漫画需要付费吗？',
      a: '不需要！韩漫网上的所有漫画作品都是完全免费的，您可以随时随地在线阅读。我们致力于让每一位韩漫爱好者都能无障碍地享受精彩的漫画内容。',
    },
    {
      q: '韩漫网的漫画多久更新一次？',
      a: '我们保持与韩国漫画官方同步更新，热门作品通常在每周固定时间更新。您可以通过订阅我们的Newsletter来获取最新的更新通知。',
    },
    {
      q: '如何找到我喜欢的漫画类型？',
      a: '您可以通过网站顶部的分类导航浏览不同类型的漫画，也可以在搜索框中直接搜索漫画名称或作者。我们提供了详细的分类标签系统，帮助您快速找到感兴趣的作品。',
    },
    {
      q: '韩漫网支持手机阅读吗？',
      a: '当然支持！韩漫网采用响应式设计，完美适配手机、平板和电脑等各种设备。无论您使用什么设备，都能获得流畅舒适的阅读体验。',
    },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMsg('感谢您的留言！我们会尽快与您联系。');
      setFormData({ name: '', email: '', subject: '', message: '' });
      return;
    }
    const fd = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9p0upqqs38sj6jc7hag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMsg('感谢您的留言！我们会尽快与您联系。');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMsg(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请稍后重试');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫网',
    url: 'https://hanmanwang.com',
    description: '韩漫网提供海量免费韩国漫画在线阅读，精选热门韩漫、最新韩漫新番，涵盖恋爱、奇幻、热血等多种类型。',
    email: 'contact@hanmanwang.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      streetAddress: '望京SOHO',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@hanmanwang.com',
      availableLanguage: 'Chinese',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero */}
          <section className="py-12 md:py-16 text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-2xl bg-primary-50 text-primary-500 mb-6">
              <i className="ri-book-open-line text-3xl"></i>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading mb-4">
              关于<strong className="text-primary-500 font-bold">韩漫网</strong>
            </h1>
            <p className="text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              韩漫网成立于2024年，致力于为中文读者打造最优质的韩国漫画在线阅读平台。我们相信好的故事应该被更多人看到，好的漫画值得被更多人分享。
            </p>
          </section>

          {/* Stats */}
          <section className="py-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: '10,000+', label: '漫画作品', icon: 'ri-stack-line' },
                { value: '500万+', label: '月活跃读者', icon: 'ri-user-smile-line' },
                { value: '98%', label: '用户好评率', icon: 'ri-thumb-up-line' },
                { value: '50+', label: '合作出版社', icon: 'ri-building-2-line' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-12 md:py-14">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-foreground-950 font-heading mb-6 text-center">
                我们的<strong className="text-primary-500 font-bold">故事</strong>
              </h2>
              <div className="space-y-4 text-foreground-600 leading-relaxed">
                <p>
                  韩漫网的创始团队是一群热爱韩国漫画的资深读者。我们发现，虽然韩国漫画在全球范围内越来越受欢迎，但中文读者能够接触到的优质韩漫资源仍然有限。许多优秀的作品因为没有良好的中文平台而被埋没。
                </p>
                <p>
                  带着"让每一部好漫画都被看到"的初心，我们创立了韩漫网。从最初的几十部作品起步，到如今拥有超过一万部韩漫作品库，我们始终坚持免费阅读的原则，让所有韩漫爱好者都能无障碍地享受阅读乐趣。
                </p>
                <p>
                  我们与多家韩国漫画出版社建立了合作关系，确保平台上的每一部作品都经过正版授权。同时，我们的翻译团队由资深的韩语翻译和中文编辑组成，力求将原作的精髓完美呈现给读者。
                </p>
                <p>
                  未来，韩漫网将继续拓展漫画资源库，优化阅读体验，并探索更多创新功能。我们希望能成为中文世界里最值得信赖的韩漫阅读平台，陪伴每一位读者走过精彩的漫画之旅。
                </p>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-12 md:py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-foreground-950 font-heading mb-8 text-center">
                常见<strong className="text-primary-500 font-bold">问题</strong>
              </h2>
              <div className="space-y-3">
                {faqs.map((faq, idx) => (
                  <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                      <h3 className="text-sm font-semibold text-foreground-800 pr-8 font-heading">{faq.q}</h3>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="py-12 md:py-14">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold text-foreground-950 font-heading mb-3 text-center">
                <strong className="text-primary-500 font-bold">联系</strong>我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                有任何问题或建议？欢迎给我们留言，我们会在24小时内回复
              </p>
              <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-4">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    required
                    rows={5}
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  />
                  <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                >
                  发送留言
                </button>
                {formStatus !== 'idle' && (
                  <p className={`text-sm text-center ${formStatus === 'success' ? 'text-accent-600' : 'text-red-500'}`}>
                    {formMsg}
                  </p>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}